<i18n lang="yaml">
ja:
  toggle: メニューを切り替え
en:
  toggle: Toggle menu
</i18n>

<template>
  <nav class="ho-admin-side-menu">
    <ul class="list">
      <li
        v-for="(section, sIndex) in items"
        :key="sIndex"
        class="section"
      >
        <button
          type="button"
          class="section-header"
          :class="{ '-open': openSections.includes(sIndex) }"
          :aria-expanded="openSections.includes(sIndex)"
          @click="toggleSection(sIndex)"
        >
          <span class="section-label">{{ section.label }}</span>
          <RiArrowDownSLine
            class="chevron"
            :class="{ '-open': openSections.includes(sIndex) }"
          />
        </button>
        <ul
          v-show="openSections.includes(sIndex)"
          class="sub-list"
        >
          <li
            v-for="(item, iIndex) in section.items"
            :key="iIndex"
            class="item"
          >
            <NuxtLink
              :to="item.to"
              class="menu-link"
              :class="{ '-active': isActive(item.to) }"
            >
              {{ item.label }}
            </NuxtLink>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>

<script lang="ts">
import RiArrowDownSLine from '~icons/ri/arrow-down-s-line'

export type SideMenuSection = {
  label: string
  items: SideMenuItem[]
}

export type SideMenuItem = {
  label: string
  to: string
  /** When true, this item is hidden for operator-level users (requires administrator role). */
  requiresAdmin?: boolean
}

export default defineComponent({
  name: 'HoAdminSideMenu',
})
</script>

<script setup lang="ts">
const route = useRoute()

const props = withDefaults(
  defineProps<{
    items: SideMenuSection[]
    defaultOpenAll?: boolean
  }>(),
  {
    defaultOpenAll: true,
  },
)

const openSections = ref<number[]>(
  props.defaultOpenAll ? props.items.map((_, i) => i) : [],
)

const toggleSection = (index: number): void => {
  const pos = openSections.value.indexOf(index)
  if (pos === -1) {
    openSections.value.push(index)
  } else {
    openSections.value.splice(pos, 1)
  }
}

const isActive = (to: string): boolean => {
  return route.path === to || route.path.startsWith(`${to}/`)
}
</script>

<style lang="scss" scoped>
@use '#base/app/assets/styles/variables' as v;
@use '#base/app/assets/styles/mixins' as m;

.ho-admin-side-menu {
  user-select: none;

  overflow-y: auto;

  width: v.$admin-sidebar-width;
  min-width: v.$admin-sidebar-width;
  height: 100%;
  border-right: 1px solid v.$admin-border-color;

  background-color: v.$admin-surface-2;

  > .list {
    padding: 8px 0;
    list-style: none;

    > .section {
      > .section-header {
        cursor: pointer;

        display: flex;
        align-items: center;
        justify-content: space-between;

        width: 100%;
        padding: 8px 16px;
        border: none;

        font-size: 11px;
        font-weight: 700;
        color: v.$admin-text-secondary;
        text-align: left;

        background: none;

        &:hover {
          color: v.$admin-text-primary;
        }

        > .chevron {
          font-size: 16px;
          transition: transform 0.2s;

          &.-open {
            transform: rotate(0deg);
          }

          &:not(.-open) {
            transform: rotate(-90deg);
          }
        }
      }

      > .sub-list {
        list-style: none;
      }
    }
  }
}

.menu-link {
  display: block;

  padding: 7px 16px 7px 24px;
  border-left: 3px solid transparent;

  font-size: 13px;
  color: v.$admin-text-primary;
  text-decoration: none;

  &:hover {
    background-color: v.$admin-surface-3;
  }

  &.-active {
    border-left-color: v.$admin-accent;
    font-weight: 600;
    color: v.$admin-accent;
    background-color: #e8f0fe;
  }
}
</style>
